<script setup>
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useMainOrdersStore } from "@/stores/mainOrders.js";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import { gregorianDate } from "@/utils/gregorianDate.js";
import StateColumn from "@/views/orders/Shared/StateColumn.vue";
import { formatPrice } from "@/utils/priceFormatter.js";
import Filters from "@/views/orders/Filters/index.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import { Icon } from "@iconify/vue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import { showMessage } from "@/utils/showMessage.js";
import { useOrderFilter } from "@/composables/useOrderFilter.js";
import { useFilterByState } from "@/composables/useFilterByShopState.js";
import { useRouter } from "vue-router";
import SortIcon from "@/components/datatable/sortIcon.vue";
import TakersPopover from "@/views/orders/Shared/TakersPopover.vue";
import { useUserStore } from "@/stores/user.js";

const takersPopover = ref();
const router = useRouter();
let interval = null;
const cleared = ref(false);
const search = computed(() => ordersStore.boostOrdersSearch);
const ordersStore = useMainOrdersStore();
const userStore = useUserStore();
const orders = computed(() => {
  return ordersStore.getOrders("boost").map((order) => ({
    ...order,
    price: order.price || order.price === 0 ? Number(order.price) : null,
    takersCut:
      order.takersCut || order.takersCut === 0 ? Number(order.takersCut) : null,
    revenue:
      order.revenue || order.revenue === 0 ? Number(order.revenue) : null,
  }));
});

const fetchNewOrders = async () => {
  if (cleared.value) return;

  if (orders.value.length > 0 && !ordersStore.isGettingNewOrders) {
    await ordersStore.action_getOrders("Boost");
  }

  if (!cleared.value) {
    interval = setTimeout(fetchNewOrders, 2000);
  }
};

async function applyFilters() {
  ordersStore.reset_lastUpdate();
  ordersStore.reset_orders("boostOrders");
  await ordersStore.action_getOrders("Boost");
}

function orderDetails({ data }) {
  const route = { name: "boost-order-details", params: { id: 0 } };
  if (data.uuid) {
    route.params.id = data.uuid;
  } else {
    route.params.id = data.id;
  }
  router.push(route);
}

function onSortChange(params) {
  const sortOrder = params.sortOrder === 1 ? "asc" : "desc";
  ordersStore.setBoostOrdersSort(params.sortField, sortOrder);
}

const { filtered } = useOrderFilter(orders, search, "boost");

const { filteredByState: filteredOrdersByShopState } = useFilterByState(
  filtered,
  computed(() => ordersStore.shopStateFilter),
  "shopState",
);

function copyUUIDs() {
  const selectedOrders =
    ordersStore.selectedBoostOrders.length > 0
      ? ordersStore.selectedBoostOrders
      : filteredOrdersByShopState.value;

  if (!selectedOrders.length) {
    alert("No UUIDs to copy!");
    return;
  }
  const uuids = selectedOrders
    .map((order) => order.uuid)
    .filter(Boolean)
    .join("\n");

  navigator.clipboard
    .writeText(uuids)
    .then(() => {
      showMessage(
        selectedOrders.length === 1
          ? `${selectedOrders.length} UUID copied`
          : `${selectedOrders.length} UUIDs copied`,
        "success",
      );
    })
    .catch((err) => {
      console.error("Failed to copy UUIDs:", err);
      showMessage("Failed to copy UUIDs", "error");
    });
}

const takersColumn = (hotOrders) => {
  if (hotOrders.length > 0) {
    if (hotOrders[0].takerName) {
      const takerName = hotOrders[0].takerName;
      if (hotOrders.length > 1) {
        return takerName + " (+" + (hotOrders.length - 1) + ")";
      }
      return takerName;
    } else {
      return "Unassigned";
    }
  }
};

const resetTableState = () => {
  sessionStorage.removeItem("boost-orders-table-state");
  window.location.reload();
};

watch(filteredOrdersByShopState, (newVal) => {
  ordersStore.boostFilteredOrders = newVal;
});

onMounted(() => {
  ordersStore.action_getOrders("Boost");
  fetchNewOrders();
});

onUnmounted(() => {
  clearTimeout(interval);
  cleared.value = true;
});
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-row justify-between gap-4">
        <Breadcrumbs />
        <div class="flex flex-riw gap-3">
          <AllButtons
            summaryPermission="ORDERS_BOOST_ACCESS_SUMMARY"
            addOrderPermission="ORDERS_BOOST_UPDATE"
            addOrderRoute="boost-order-add"
            summary="BoostSummary"
            :applyFilter="applyFilters"
            v-model="ordersStore.boostOrdersSearch"
            @copyUUIDs="copyUUIDs"
          />
          <button
            v-if="userStore.hasPermission('BIG_BOSS')"
            @click="resetTableState"
            class="btn btn-outline-dark p-1 text-white h-[32px]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 15 15"
            >
              <path d="M0 0h15v15H0z" fill="none" />
              <path
                fill="currentColor"
                d="M4.225 2.082a.5.5 0 0 1 .693.694l-.065.078L3.707 4H9a4.5 4.5 0 0 1 0 9H5a.5.5 0 1 1 0-1h4a3.5 3.5 0 1 0 0-7H3.707l1.146 1.147l.065.078a.5.5 0 0 1-.693.693l-.079-.064l-2-2a.5.5 0 0 1 0-.707l2-2z"
              />
            </svg>
          </button>
        </div>
      </div>
      <div
        v-if="ordersStore.isLoading"
        class="flex items-center justify-center h-60"
      >
        <Icon icon="eos-icons:bubble-loading" class="size-12" />
      </div>
      <DataTable
        v-else
        :nullSortOrder="-1"
        :value="filteredOrdersByShopState"
        scrollable
        scrollHeight="flex"
        :virtualScrollerOptions="{ itemSize: 32 }"
        resizableColumns
        stateStorage="session"
        stateKey="boost-orders-table-state"
        removableSort
        stripedRows
        class="text-xs w-full"
        size="small"
        tableStyle="width: 100%; table-layout: fixed;"
        :sortField="ordersStore.boostOrdersSort?.field"
        :sortOrder="ordersStore.boostOrdersSort?.type === 'asc' ? 1 : -1"
        row-hover
        @row-click="orderDetails"
        @sort="onSortChange"
        dataKey="id"
        v-model:selection="ordersStore.selectedBoostOrders"
        :rowClass="
          () =>
            'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'
        "
      >
        <Column
          selectionMode="multiple"
          style="min-width: 36px; max-width: 36px; width: 3%"
          header-class="min-w-[36px] max-w-[36px] w-[36px]"
        ></Column>
        <Column
          field="uuid"
          header="UUID"
          :sortable="true"
          header-class="!border-r"
          class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
          style="width: 6%; min-width: 66px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <div @click.stop class="flex items-center h-full overflow-hidden">
              <span class="truncate">{{ slotProps.data.uuid }}</span>
            </div>
          </template>
        </Column>
        <Column
          field="store"
          header="Store"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 53px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column
          field="game"
          header="Game"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 53px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column
          field="g2gSellID"
          header="Sell ID"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
          style="width: 6%; min-width: 58px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span
              @click.stop
              class="w-full block h-full py-[0.375rem] truncate"
              >{{ slotProps.data.g2gSellID }}</span
            >
          </template>
        </Column>
        <Column
          field="title"
          header="Title"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 15%; min-width: 46px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column
          field="customerName"
          header="Customer"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 7%; min-width: 73px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.customerName
                ? slotProps.data.customerName
                : slotProps.data.customer
                  ? slotProps.data.customer
                  : ""
            }}
          </template>
        </Column>
        <Column
          field="hotOrders"
          header="Takers"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
          style="width: 10%; min-width: 100px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span
              @click.stop
              class="w-full block h-full py-[0.375rem] truncate"
              @mouseover="
                takersPopover.displayHotOrders($event, slotProps.data)
              "
              @mouseleave="takersPopover.hidePopover()"
            >
              {{ takersColumn(slotProps.data.hotOrders) }}
            </span>
          </template>
        </Column>
        <Column
          field="price"
          header="Price"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 50px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.price || slotProps.data.price === 0
                ? formatPrice(slotProps.data.price) + " $"
                : ""
            }}
          </template>
        </Column>
        <Column
          field="takersCut"
          header="Cut"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap text-red-400 cursor-pointer"
          style="width: 6%; min-width: 42px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            {{
              slotProps.data.takersCut || slotProps.data.takersCut === 0
                ? formatPrice(slotProps.data.takersCut) + " $"
                : ""
            }}
          </template>
        </Column>
        <Column
          field="revenue"
          header="Revenue"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 69px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span
              :class="{
                '!text-red-400': slotProps.data.revenue < 0,
                '!text-success-500': slotProps.data.revenue > 0,
              }"
            >
              {{
                slotProps.data.revenue || slotProps.data.revenue === 0
                  ? formatPrice(slotProps.data.revenue) + " $"
                  : ""
              }}
            </span>
          </template>
        </Column>
        <Column
          field="state"
          header="Status"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 5%; min-width: 56px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <div class="flex justify-start">
              <StateColumn :state="slotProps.data.state" />
            </div>
          </template>
        </Column>
        <Column
          field="shopState"
          header="Shop State"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 5%; min-width: 82px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
        </Column>
        <Column
          field="lastUpdate"
          header="Last Update"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 90px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max truncate">
              {{ gregorianDate(slotProps.data.lastUpdate, "DD MMM HH:mm") }}
            </span>
          </template>
        </Column>
        <Column
          field="creationTime"
          header="Creation Time"
          dataType="numeric"
          :sortable="true"
          class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
          style="width: 6%; min-width: 100px; height: 32px"
        >
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder" />
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max truncate">
              {{ gregorianDate(slotProps.data.creationTime, "DD MMM YYYY") }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
      <TakersPopover ref="takersPopover" />
    </div>
    <Filters
      v-if="ordersStore.isShowFilterModal"
      @applyFilter="applyFilters"
      :filters="['stores', 'games', 'states', 'shopStates']"
      service="Boost"
    />
  </div>
</template>
